import { useRef, useState, type ChangeEvent } from "react";
import type { FieldValues } from "react-hook-form";

import Icons from "assets";
import { Checkbox } from "ui/checkbox";
import { DatePicker } from "ui/date-picker";
import { Button } from "ui/button";
import { IconButton } from "ui/icon-button";
import { Radio } from "ui/radio";
import { Select, OptionType } from "ui/select";
import { TextArea } from "ui/text-area";
import { TextField, MaskedTextField } from "ui/text-field";
import { Switch } from "ui/switch";
import { FieldErrorMessage, FieldHelperText, FieldLabel } from "ui/forms";

import FormBuilderUploadPreview from "containers/admin/clients/client/components/form-builder/form-builder-upload-preview";
import type { ControllerFieldRendererParams } from "containers/admin/clients/client/components/form-builder/form-builder-field.types";
import {
  getConditionalRules,
  getFieldDisabledOptions,
  getUploadConfig,
  isFieldClearable,
  type JsonFormOptionValue,
} from "containers/admin/clients/client/components/form-builder/form-schema";
import { getFieldGenerationTooltip } from "containers/admin/clients/client/components/form-builder/form-builder-utils";
import { toDayjs, toFormDate } from "utilities/date";

/**
 * TODO: replace once a strategy/asset upload endpoint exists (there is none
 * today — only the Theme-specific `/api/clients/{id}/theme-assets`, see
 * `uploadThemeAsset` in `hooks/api/use-theme.ts` for the shape to match: POST
 * multipart, returns a persisted URL). Until then this just creates a local
 * object URL so the upload UI works end-to-end without a backend.
 */
const uploadFileStub = async (file: File): Promise<string> => URL.createObjectURL(file);

const fileMatchesAccept = (file: File, accept?: string): boolean => {
  const patterns = (accept ?? "")
    .split(",")
    .map((pattern) => pattern.trim().toLowerCase())
    .filter(Boolean);

  if (patterns.length === 0) {
    return true;
  }

  const fileName = file.name.toLowerCase();
  const fileType = file.type.toLowerCase();

  return patterns.some((pattern) => {
    if (pattern.startsWith(".")) {
      return fileName.endsWith(pattern);
    }

    if (pattern.endsWith("/*")) {
      return fileType.startsWith(pattern.slice(0, -1));
    }

    return fileType === pattern;
  });
};

const renderTextareaField = <T extends FieldValues>({
  commonProps,
  controllerField: { onChange, value, ref },
}: ControllerFieldRendererParams<T>) => (
  <TextArea
    {...commonProps}
    value={(value ?? "") as string}
    onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
      onChange(event.target.value);
    }}
    ref={ref}
  />
);

const renderSelectField = <T extends FieldValues>({
  field,
  parentSchema,
  name,
  fieldName,
  label,
  options,
  commonProps,
  controllerField: { onChange, value, ref },
}: ControllerFieldRendererParams<T>) => (
  <Select
    {...commonProps}
    selectName={fieldName}
    ariaLabel={label}
    options={options as OptionType[]}
    disableClearable={!isFieldClearable(field)}
    value={options.find((opt) => opt.value === value)}
    onChange={(val) => onChange(val?.value)}
    infoTooltip={getFieldGenerationTooltip(parentSchema, name)}
    ref={ref}
  />
);

const renderRadioGroupField = <T extends FieldValues>({
  field,
  parentSchema,
  name,
  prefix,
  fieldName,
  label,
  options,
  error,
  renderField,
  commonProps: { size },
  controllerField: { onChange, value },
}: ControllerFieldRendererParams<T>) => {
  const radioConditionalRules = getConditionalRules(parentSchema).filter(
    (rule) => rule.triggerFieldName === name,
  );
  const selectedValue = String(value ?? "");

  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel name={fieldName} size={size}>
        {label}
      </FieldLabel>
      <div className="flex flex-col gap-1.5">
        {options.map((opt) => {
          const inlineDependents = radioConditionalRules
            .filter((rule) => String(rule.showIf) === String(opt.value))
            .flatMap((rule) => rule.dependentFieldNames);

          return (
            <div key={String(opt.value)} className="flex flex-col gap-1.5">
              <Radio
                label={opt.label}
                value={String(opt.value)}
                checked={selectedValue === String(opt.value)}
                name={fieldName}
                onChange={onChange}
              />
              {selectedValue === String(opt.value) &&
                inlineDependents.map((dependentFieldName) => {
                  const dependentField = parentSchema.properties[dependentFieldName];

                  return dependentField
                    ? renderField(dependentFieldName, dependentField, parentSchema, prefix)
                    : null;
                })}
            </div>
          );
        })}
      </div>
      {!!field.description && !error && <FieldHelperText message={field.description} />}
      {!!error && <FieldErrorMessage name={fieldName} message={error.message as string} />}
    </div>
  );
};

const renderMultiSelectField = <T extends FieldValues>({
  field,
  parentSchema,
  name,
  fieldName,
  label,
  options,
  commonProps,
  controllerField: { onChange, value, ref },
}: ControllerFieldRendererParams<T>) => (
  <Select
    {...commonProps}
    selectName={fieldName}
    ariaLabel={label}
    options={options as OptionType[]}
    multiple
    disableClearable={!isFieldClearable(field)}
    value={(options as OptionType[]).filter((opt) =>
      ((value as JsonFormOptionValue[] | undefined) ?? []).includes(opt.value),
    )}
    onChange={(selected) => onChange(selected.map((option) => option.value))}
    infoTooltip={getFieldGenerationTooltip(parentSchema, name)}
    ref={ref}
  />
);

const renderToggleField = <T extends FieldValues>({
  label,
  controllerField: { onChange, value },
}: ControllerFieldRendererParams<T>) => (
  <div className="py-2">
    <Switch toggleName={label} checked={!!value} onChange={onChange} />
  </div>
);

const UploadField = <T extends FieldValues>({
  field,
  label,
  fieldName,
  commonProps: { size },
  controllerField: { onChange, value },
}: ControllerFieldRendererParams<T>) => {
  const config = getUploadConfig(field);
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string>();
  const urlValue = typeof value === "string" ? value : "";

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    event.target.value = "";

    if (!file) {
      return;
    }

    if (!fileMatchesAccept(file, config.accept)) {
      setUploadError(`Only ${config.acceptLabel ?? "supported"} files are allowed`);
      return;
    }

    if (config.maxSizeKb && file.size > config.maxSizeKb * 1024) {
      setUploadError(`File must be ${config.maxSizeKb} KB or smaller`);
      return;
    }

    setUploadError(undefined);

    try {
      const url = await uploadFileStub(file);

      onChange(url);
    } catch {
      setUploadError("Could not upload file. Please try again.");
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {!!label.trim() && (
        <FieldLabel name={fieldName} size={size}>
          {label}
        </FieldLabel>
      )}
      <div className="flex items-center gap-1">
        {urlValue ? (
          <>
            <FormBuilderUploadPreview
              src={urlValue}
              alt={label}
              background={config.previewBackground}
            />
            <IconButton
              variant="text"
              size="small"
              aria-label={`Remove ${label}`}
              onClick={() => onChange("")}>
              <Icons.Trash />
            </IconButton>
          </>
        ) : (
          <Button
            type="button"
            variant="outlined"
            size="small"
            startIcon={<Icons.Upload />}
            onClick={() => inputRef.current?.click()}>
            Upload
          </Button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={config.accept}
        className="hidden"
        onChange={handleFileChange}
      />
      {uploadError && <FieldErrorMessage name={fieldName} message={uploadError} />}
      {config.showRequirements && (
        <div className="flex gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-sm font-bold text-gray-500">Format:</span>
            <span className="text-sm text-gray-900">{config.acceptLabel}</span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm font-bold text-gray-500">Maximum file size:</span>
            <span className="text-sm text-gray-900">{config.maxSizeKb} KB</span>
          </div>
        </div>
      )}
    </div>
  );
};

const renderCheckboxGroupField = <T extends FieldValues>({
  field,
  fieldName,
  label,
  options,
  showFieldLabel,
  error,
  commonProps: { size },
  controllerField: { onChange, value },
}: ControllerFieldRendererParams<T>) => {
  const disabledOptions = new Set(getFieldDisabledOptions(field));
  const currentValues = (value as JsonFormOptionValue[] | undefined) ?? [];

  return (
    <div className="flex flex-col gap-1.5">
      {showFieldLabel && (
        <FieldLabel name={fieldName} size={size}>
          {label}
        </FieldLabel>
      )}
      {/* The checkbox no longer carries MUI's 9px padding, which used to space these rows. */}
      <div className="flex flex-col gap-2">
        {options.map((opt) => {
          const isDisabled = disabledOptions.has(opt.value);
          const isChecked = currentValues.includes(opt.value);

          return (
            <Checkbox
              key={String(opt.value)}
              label={opt.label}
              size="sm"
              checked={isChecked}
              disabled={isDisabled}
              onChange={(isNowChecked) => {
                const nextValues = isNowChecked
                  ? [...currentValues, opt.value]
                  : currentValues.filter((v) => v !== opt.value);

                onChange(nextValues);
              }}
            />
          );
        })}
      </div>
      {!!field.description && !error && <FieldHelperText message={field.description} />}
      {!!error && <FieldErrorMessage name={fieldName} message={error.message as string} />}
    </div>
  );
};

const renderTextField = <T extends FieldValues>({
  uiType,
  parentSchema,
  name,
  mask,
  commonProps,
  controllerField: { onChange, value, ref, onBlur },
}: ControllerFieldRendererParams<T>) => {
  if (uiType === "text" && mask) {
    return (
      <MaskedTextField
        {...commonProps}
        mask={mask}
        value={(value ?? "") as string}
        onChange={onChange}
        onBlur={onBlur}
        ref={ref}
      />
    );
  }

  return (
    <TextField
      {...commonProps}
      type={uiType === "number" ? "number" : "text"}
      value={(value ?? "") as string}
      onChange={(event: ChangeEvent<HTMLInputElement>) => {
        if (uiType === "number") {
          onChange(event.target.value === "" ? undefined : Number(event.target.value));
          return;
        }

        onChange(event.target.value);
      }}
      infoTooltip={getFieldGenerationTooltip(parentSchema, name)}
      ref={ref}
    />
  );
};

const renderDateField = <T extends FieldValues>({
  parentSchema,
  name,
  commonProps: { placeholder: _placeholder, ...commonProps },
  controllerField: { onChange, value, ref, onBlur },
}: ControllerFieldRendererParams<T>) => (
  <DatePicker
    {...commonProps}
    value={toDayjs(value as string | undefined)}
    onChange={(date) => onChange(toFormDate(date))}
    onClear={() => onChange("")}
    onBlur={onBlur}
    infoTooltip={getFieldGenerationTooltip(parentSchema, name)}
    ref={ref}
  />
);

export const renderControllerField = <T extends FieldValues>(
  params: ControllerFieldRendererParams<T>,
) => {
  switch (params.uiType) {
    case "textarea":
      return renderTextareaField(params);
    case "select":
    case "select-search":
      return renderSelectField(params);
    case "radio-group":
      return renderRadioGroupField(params);
    case "multi-select":
      return renderMultiSelectField(params);
    case "checkbox":
    case "toggle":
      return renderToggleField(params);
    case "checkbox-group":
      return renderCheckboxGroupField(params);
    case "upload":
      return <UploadField {...params} />;
    case "date":
      return renderDateField(params);
    case "number":
    case "text":
    default:
      return renderTextField(params);
  }
};
